import React, { useState, useEffect } from 'react';
import {
  Modal, IconClose, IconArrow,
  IconChat,
} from 'gatorcomponents';
import useLocale from '@/hooks/useLocale';
import { useAiChat } from '@/hooks/AiChat';
import { gatorAnalytics } from '@/analytics/gator';
import slide1 from '@/media/gatorModal/gator-modal-slide-1.png';
import slide2 from '@/media/gatorModal/gator-modal-slide-2.png';
import slide3 from '@/media/gatorModal/gator-modal-slide-3.png';
import type {
  GatorPresentationModalProps,
  GatorPresentationModalLocale,
} from './GatorPresentationModal.types';
import * as Styles from './GatorPresentationModal.styles';

const slideImages = [slide1, slide2, slide3];

const GatorPresentationModal = ({ open, onClose }: GatorPresentationModalProps) => {
  const { aichat } = useLocale() as {
    aichat: { presentationModal: GatorPresentationModalLocale };
  };
  const { presentationModal } = aichat;
  const { handleInitialInteraction } = useAiChat();

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (open) {
      setCurrentSlide(0);
      gatorAnalytics.gatorPresentationModalOpen();
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const slide = presentationModal.slides[currentSlide];
  const isLastSlide = currentSlide === presentationModal.slides.length - 1;

  const handleAdvance = () => {
    gatorAnalytics.gatorPresentationModalSlide({ slide: currentSlide + 2 });
    setCurrentSlide(prev => prev + 1);
  };

  const handleClose = () => {
    gatorAnalytics.gatorPresentationModalClose();
    onClose();
  };

  const handleCtaClick = () => {
    gatorAnalytics.gatorPresentationModalCta();
    onClose();
    handleInitialInteraction({ fullScreen: true, generateThreadId: true });
  };

  return (
    <Modal
      noContentPadding
      maxWidth={552}
      maxHeight={640}
      testId="gator-presentation-modal"
    >
      <Styles.Wrapper
        role="dialog"
        aria-modal="true"
        aria-labelledby="gator-presentation-modal-title"
        aria-describedby="gator-presentation-modal-description"
      >
        <Styles.Header>
          <Styles.Dots>
            {presentationModal.slides.map((item, index) => (
              <Styles.Dot
                key={item.title}
                $filled={index <= currentSlide}
                data-testid="gator-presentation-modal-dot"
              />
            ))}
          </Styles.Dots>
          <Styles.CloseButton
            type="button"
            onClick={handleClose}
            aria-label={presentationModal.closeLabel}
            data-testid="gator-presentation-modal-close"
          >
            <IconClose size="md" color="primaryExtraDark" />
          </Styles.CloseButton>
        </Styles.Header>

        <Styles.IllustrationArea>
          <Styles.Illustration src={slideImages[currentSlide]} alt="" />
        </Styles.IllustrationArea>

        <Styles.BodyContent>
          <Styles.TextContent>
            <Styles.Title id="gator-presentation-modal-title">{slide.title}</Styles.Title>
            <Styles.Description id="gator-presentation-modal-description">
              {slide.description}
            </Styles.Description>
          </Styles.TextContent>

          <Styles.Footer>
            {isLastSlide ? (
              <Styles.CtaButton
                type="button"
                onClick={handleCtaClick}
                data-testid="gator-presentation-modal-cta"
              >
                <IconChat size="md" color="highPure" />
                <span>{presentationModal.ctaLabel}</span>
              </Styles.CtaButton>
            ) : (
              <Styles.AdvanceButton
                type="button"
                onClick={handleAdvance}
                aria-label={presentationModal.nextLabel}
                data-testid="gator-presentation-modal-advance"
              >
                <IconArrow direction="right" size="sm" color="highPure" />
              </Styles.AdvanceButton>
            )}
          </Styles.Footer>
        </Styles.BodyContent>
      </Styles.Wrapper>
    </Modal>
  );
};

export default GatorPresentationModal;
